import { useContext } from 'react';
import * as Yup from 'yup';
import { i18n } from '../utils/setupTranslations';
import { AuthenticationContext } from '../contexts/AuthenticationContext';
import { NewPackFormKeys } from '../pages/NewPack/NewPack.types';
import { getFileSizeInMb } from '../helpers/conversion';

const MIN_ARTWORK_HEIGHT = 1000;
const MAX_FILESIZE_MB = 4;

const packArtworkErrorMessage = i18n._(
    'Artwork must be a JPG or PNG file, minimum 1000 x 1000 pixels and a perfect square',
);
const packCsvErrorMessage = i18n._('Pack metadata file must be a CSV file.');
const packDemoErrorMessage = i18n._(
    'Pack demo must be a wav or mp3 file and 30 seconds or longer.',
);
const samplesZipErrorMessage = i18n._(
    'Samples folder must be a zip and cannot exceed 2G in size',
);
const packArtworkFileSizeErrorMessage = i18n._(
    'The artwork file is too large. Please upload a file not exceeding 4MB in size.',
);

// Validation Shape
export const useNewPackFormSchema = (): Yup.ObjectSchema<
    object & {
        name: string;
        description: string | undefined;
        labelName: string;
        genre: string | undefined;
        exclusivity: string;
        useSfxLexicon: string;
        csvUpload: any;
        demoUpload: any;
        artworkUpload: any;
        zipUpload: any;
    }
> => {
    const { isLandr, labels, canUploadCsv } = useContext(AuthenticationContext);
    const isLabelRequired = !isLandr && labels?.length > 0;

    const validationShape = {
        [NewPackFormKeys.Name]: Yup.string().trim().required('Required'),
        [NewPackFormKeys.Description]: Yup.string().trim().required('Required'),
        [NewPackFormKeys.LabelName]: Yup.lazy(() => {
            if (isLabelRequired) {
                return Yup.string().trim().required('Required');
            }
            return Yup.string().trim().notRequired();
        }),
        [NewPackFormKeys.Genre]: Yup.string().trim().required('Required'),
        [NewPackFormKeys.Exclusivity]: Yup.string().required('Required'),
        [NewPackFormKeys.Lexicon]: Yup.lazy(() => {
            if (!canUploadCsv) {
                return Yup.string().trim().required('Required');
            }
            return Yup.string().trim().notRequired();
        }),

        [NewPackFormKeys.CsvUpload]: Yup.lazy(() => {
            if (canUploadCsv) {
                return Yup.mixed()
                    .required('Required')
                    .test(
                        'is-csv',
                        packCsvErrorMessage,
                        (value: File | undefined) => {
                            if (!canUploadCsv || value === undefined) {
                                return Boolean(true);
                            }
                            return Boolean(
                                value?.type.match(
                                    /(text\/csv|text\/x-csv|text\/plain|application\/vnd\.ms-excel|text\/tab-separated-values)/,
                                ) || value?.name.includes('.csv'),
                            );
                        },
                    );
            }
            return Yup.string().trim().notRequired();
        }),
        [NewPackFormKeys.DemoUpload]: Yup.mixed()
            .required('Required')
            .test(
                'is-mp3-or-wav',
                packDemoErrorMessage,
                (value: File | undefined) =>
                    Boolean(
                        value?.type.match(
                            /(audio\/mpeg|audio\/wav|audio\/mp3)/,
                        ),
                    ),
            ),
        [NewPackFormKeys.ArtworkUpload]: Yup.mixed()
            .required('Required')
            .test(
                'is-jpeg-or-png',
                packArtworkErrorMessage,
                (value: File | undefined) =>
                    Boolean(value?.type.match(/(image\/jpeg|image\/png)/)),
            )
            .test(
                'exceeds-mb-threshold',
                packArtworkFileSizeErrorMessage,
                (value: File | undefined) =>
                    Boolean(
                        value && getFileSizeInMb(value?.size) < MAX_FILESIZE_MB,
                    ),
            )
            .test('is-square-image', packArtworkErrorMessage, function (
                value: File | undefined,
            ): Promise<boolean> {
                const fr = new FileReader();

                if (!value) {
                    return Promise.resolve(false);
                }

                return new Promise<boolean>((resolve) => {
                    fr.onload = (): void => {
                        const img = new Image();

                        img.onload = (): void => {
                            if (img.width !== img.height) {
                                return resolve(false);
                            }
                            if (img.height < MIN_ARTWORK_HEIGHT) {
                                return resolve(false);
                            }
                            return resolve(true);
                        };

                        const { result } = fr;

                        if (!result || typeof result === 'object') {
                            return resolve(false);
                        }

                        img.src = result; // is the data URL because called with readAsDataURL
                    };

                    fr.readAsDataURL(value);
                });
            }),
        [NewPackFormKeys.ZipUpload]: Yup.mixed()
            .required('Required')
            .test('is-zip', samplesZipErrorMessage, (value: File | undefined) =>
                Boolean(
                    value?.type.match(/application\/zip/) ||
                        value?.type.match(/application\/octet-stream/) ||
                        value?.type.match(/application\/x-zip-compressed/),
                ),
            ),
    };

    return Yup.object().shape(validationShape);
};
